import React, { useState, useEffect, useRef, useCallback } from 'react';
import { playRestFinishedAlert, unlockAudioAlert } from '../lib/restAlert';
import { schedulePushForRest, cancelPushForRest, getRestPushState, serverAlreadyCoveredRest } from '../lib/restPush';
import { announceRestFinish } from '../lib/restFinishGate';

const STORAGE_KEY = 'coachfit_rest_timer';

// Azzera lo stato persistito del recupero. Da chiamare PRIMA di avviare un
// nuovo timer (o alla chiusura del workout): senza questo, il remount del
// componente "ripristina" il countdown precedente da localStorage (vecchia
// scadenza, vecchio timerId, push non riprogrammata) invece di ripartire.
export const clearPersistedRestTimer = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* ignore */ }
};

// Id univoco del countdown: la Web Push programmata lato server viene inviata
// solo se al momento dello scadere l'id è ancora quello attivo (annulla/
// sostituisci/riavvia = nuovo id o riga cancellata → nessuna notifica vecchia).
const newTimerId = () => `rt-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

function RestTimer({ isActive, onClose, initialSeconds = 90 }) {
  const [remaining, setRemaining] = useState(initialSeconds);
  const [isPaused, setIsPaused] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const endTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const timerIdRef = useRef(null);

  // Annuncio di fine recupero: percorso UNICO condiviso da tick / visibility /
  // mount (PB-003). Idempotente e durevole per timerId (vedi restFinishGate):
  // - se la push server ha già coperto questo timerId → nessun avviso client;
  // - altrimenti (push off / non inviata / fallita / dubbio) → avvisa UNA volta
  //   e annulla la push pendente. La deduplicazione sopravvive a resume/remount.
  const doAnnounceFinish = useCallback((tid) => {
    const timerId = tid || timerIdRef.current;
    return announceRestFinish(timerId, {
      getRestPushState,
      serverAlreadyCoveredRest,
      playAlert: playRestFinishedAlert,
      cancelPush: cancelPushForRest,
    });
  }, []);

  // --- localStorage persistence ---
  const saveState = useCallback((state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* ignore */ }
  }, []);

  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* ignore */ }
  }, []);

  const loadState = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  // --- Tick: recalculate from endTime ---
  const tick = useCallback(() => {
    if (!endTimeRef.current) return;

    const now = Date.now();
    const left = Math.ceil((endTimeRef.current - now) / 1000);

    if (left <= 0) {
      setRemaining(0);
      setIsFinished(true);
      doAnnounceFinish(timerIdRef.current); // percorso unico (gate) — anche il tick passa da qui
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      clearState();
    } else {
      setRemaining(left);
    }
  }, [doAnnounceFinish, clearState]);

  // --- Start interval ---
  const startTicking = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(tick, 250);
    tick(); // immediate first tick
  }, [tick]);

  // --- Init: when isActive changes ---
  useEffect(() => {
    if (!isActive) {
      // Deactivating: clean up
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Activating: check localStorage for a running timer
    const saved = loadState();
    if (saved && saved.endTime && saved.endTime > Date.now()) {
      // Restore running timer: la push è GIÀ programmata (stesso timerId salvato)
      endTimeRef.current = saved.endTime;
      timerIdRef.current = saved.timerId || null;
      setIsPaused(false);
      setIsFinished(false);
      startTicking();
    } else if (saved && saved.isPaused && saved.pausedRemaining > 0) {
      // Restore paused timer (push annullata alla pausa)
      endTimeRef.current = null;
      timerIdRef.current = saved.timerId || null;
      setRemaining(saved.pausedRemaining);
      setIsPaused(true);
      setIsFinished(false);
    } else if (saved && saved.endTime && saved.endTime <= Date.now()) {
      // Timer salvato GIÀ SCADUTO (recupero finito mentre eravamo via / remount):
      // NON ripartire come fresh. Finalizza come finito e passa dal gate unico,
      // che non riprodurrà un secondo avviso se il server ha già notificato (PB-003).
      endTimeRef.current = saved.endTime;
      timerIdRef.current = saved.timerId || null;
      setRemaining(0);
      setIsPaused(false);
      setIsFinished(true);
      clearState();
      doAnnounceFinish(saved.timerId);
    } else {
      // Fresh timer → programma la Web Push per la scadenza
      endTimeRef.current = Date.now() + initialSeconds * 1000;
      timerIdRef.current = newTimerId();
      setRemaining(initialSeconds);
      setIsPaused(false);
      setIsFinished(false);
      saveState({ endTime: endTimeRef.current, isPaused: false, pausedRemaining: null, timerId: timerIdRef.current });
      startTicking();
      schedulePushForRest(endTimeRef.current, timerIdRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, initialSeconds]);

  // --- Visibility change: resync on return ---
  useEffect(() => {
    if (!isActive) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Resync from endTime or localStorage
        if (isPaused) return; // paused, nothing to resync

        const saved = loadState();
        if (saved && saved.endTime) {
          endTimeRef.current = saved.endTime;
          const now = Date.now();
          const left = Math.ceil((saved.endTime - now) / 1000);
          if (left <= 0) {
            setRemaining(0);
            setIsFinished(true);
            clearState();
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Recupero scaduto mentre eravamo via: annuncio UNICO via gate
            // (coordinato con la push server, idempotente per timerId) (PB-003).
            doAnnounceFinish(saved.timerId);
          } else {
            setRemaining(left);
            if (!intervalRef.current) {
              startTicking();
            }
          }
        }
      }
    };

    const handlePageShow = (e) => {
      if (e.persisted) {
        handleVisibility();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isActive, isPaused, loadState, doAnnounceFinish, clearState, startTicking]);

  // --- Controls ---
  // Ogni tap sui controlli è un gesto utente → occasione per sbloccare l'audio iOS
  const handlePauseResume = () => {
    unlockAudioAlert();
    if (isPaused) {
      // Resume: set new endTime from remaining → riprogramma la push
      const newEnd = Date.now() + remaining * 1000;
      endTimeRef.current = newEnd;
      timerIdRef.current = newTimerId();
      setIsPaused(false);
      saveState({ endTime: newEnd, isPaused: false, pausedRemaining: null, timerId: timerIdRef.current });
      startTicking();
      schedulePushForRest(newEnd, timerIdRef.current);
    } else {
      // Pause: save remaining, clear interval → annulla la push programmata
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      endTimeRef.current = null;
      setIsPaused(true);
      saveState({ endTime: null, isPaused: true, pausedRemaining: remaining, timerId: timerIdRef.current });
      cancelPushForRest();
    }
  };

  const handleReset = () => {
    unlockAudioAlert();
    const newEnd = Date.now() + initialSeconds * 1000;
    endTimeRef.current = newEnd;
    timerIdRef.current = newTimerId(); // nuovo id → non soppresso dal gate del recupero precedente
    setRemaining(initialSeconds);
    setIsPaused(false);
    setIsFinished(false);
    saveState({ endTime: newEnd, isPaused: false, pausedRemaining: null, timerId: timerIdRef.current });
    startTicking();
    schedulePushForRest(newEnd, timerIdRef.current); // nuovo id → la vecchia push muore
  };

  const handleAddTime = (seconds) => {
    unlockAudioAlert();
    if (isFinished) {
      // Restart with extra time → nuova push
      const newEnd = Date.now() + seconds * 1000;
      endTimeRef.current = newEnd;
      timerIdRef.current = newTimerId(); // nuovo id → non soppresso dal gate del recupero appena concluso
      setRemaining(seconds);
      setIsFinished(false);
      setIsPaused(false);
      saveState({ endTime: newEnd, isPaused: false, pausedRemaining: null, timerId: timerIdRef.current });
      startTicking();
      schedulePushForRest(newEnd, timerIdRef.current);
    } else if (isPaused) {
      const newRemaining = remaining + seconds;
      setRemaining(newRemaining);
      saveState({ endTime: null, isPaused: true, pausedRemaining: newRemaining, timerId: timerIdRef.current });
    } else {
      // Running: extend endTime → riprogramma la push sulla nuova scadenza
      const newEnd = (endTimeRef.current || Date.now()) + seconds * 1000;
      endTimeRef.current = newEnd;
      timerIdRef.current = newTimerId();
      saveState({ endTime: newEnd, isPaused: false, pausedRemaining: null, timerId: timerIdRef.current });
      schedulePushForRest(newEnd, timerIdRef.current);
    }
  };

  const handleClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    endTimeRef.current = null;
    clearState();
    cancelPushForRest(); // recupero annullato → nessuna notifica
    onClose();
  };

  // --- Formatting ---
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return ((initialSeconds - remaining) / initialSeconds) * 100;
  };

  const getTimerColor = () => {
    if (isFinished) return 'bg-success-500';
    if (remaining <= 10) return 'bg-red-500';
    if (remaining <= 30) return 'bg-primary-500';
    return 'bg-accent-500';
  };

  const getTextColor = () => {
    if (isFinished) return 'text-success-600';
    if (remaining <= 10) return 'text-red-600';
    if (remaining <= 30) return 'text-primary-600';
    return 'text-accent-600';
  };

  if (!isActive) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 bg-white shadow-xl border-b-4 border-accent-500 animate-slide-down"
         style={{ zIndex: 99999 }}>
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          {/* Timer display */}
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className={`text-3xl font-mono font-bold tabular-nums ${getTextColor()}`}>
                {formatTime(remaining)}
              </div>
              {isFinished && (
                <div className="flex items-center gap-2 px-3 py-1 bg-success-100 text-success-700 rounded-full text-sm font-semibold animate-pulse">
                  Recupero completato!
                </div>
              )}
              {!isFinished && isPaused && (
                <div className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-semibold">
                  IN PAUSA
                </div>
              )}
            </div>

            {/* Progress bar */}
            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ease-linear ${getTimerColor()}`}
                style={{ width: `${Math.min(getProgressPercentage(), 100)}%` }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* +15s button */}
            <button
              onClick={() => handleAddTime(15)}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold text-gray-700 transition-colors"
              title="Aggiungi 15 secondi"
            >
              +15
            </button>

            {/* Pause/Resume */}
            <button
              onClick={handlePauseResume}
              className="w-10 h-10 flex items-center justify-center bg-accent-100 hover:bg-accent-200 rounded-lg transition-colors"
              title={isPaused ? "Riprendi" : "Pausa"}
            >
              {isPaused ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-accent-700"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-accent-700"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              )}
            </button>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="w-10 h-10 flex items-center justify-center bg-primary-100 hover:bg-primary-200 rounded-lg transition-colors"
              title="Ricomincia"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary-700"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </button>

            {/* Close */}
            <button
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
              title="Chiudi timer"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-600"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RestTimer;
